import React from 'react';
import { motion } from 'framer-motion';
import { Settings, LogOut, Key, Search, Plus, Folder, Trash2, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useManuals } from '../../contexts/ManualsContext';

export const Sidebar = () => {
  const { darkMode } = useTheme();
  const { userRole, currentUser, logout, setIsChangingPassword } = useAuth();
  const {
    categories,
    manuals,
    activeCategory,
    activeManual,
    searchQuery, setSearchQuery,
    filteredManuals,
    handleCategoryClick,
    handleManualClick,
    isSidebarOpen, setIsSidebarOpen,
    setManualToDelete,
    setShowDeleteManualModal,
    setCategoryToDelete,
    setIsAddingManual,
    setIsAddingCategory,
  } = useManuals();

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <>
      <motion.div
        initial={{ x: -300 }}
        animate={{ x: isSidebarOpen ? 0 : -300 }}
        transition={{ type: "tween", duration: 0.2 }}
        className={`fixed md:static z-40 h-full flex flex-col ${
          darkMode ? 'bg-gray-800 border-r border-gray-700' : 'bg-white shadow-xl md:shadow-none border-r border-gray-200'
        } w-[85vw] max-w-[300px] md:w-64`}
      >
        {/* Шапка */}
        <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between`}>
          <div className="flex items-center space-x-3">
            <div className={`${darkMode ? 'bg-blue-900' : 'bg-blue-100'} p-2 rounded-lg`}>
              <Settings className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            </div>
            <div>
              <h1 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-gray-800'}`}>Система управления</h1>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Версия 2.7</p>
            </div>
          </div>
          <button
            onClick={toggleSidebar}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Закрыть меню"
          >
            <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Поиск */}
        <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Поиск по мануалам..."
              className={`w-full pl-10 pr-4 py-2 text-sm rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'border border-gray-300'
              }`}
            />
            <Search className={`absolute left-3 top-2.5 w-4 h-4 ${
              darkMode ? 'text-gray-400' : 'text-gray-400'
            }`} />
          </div>
        </div>

        {/* Навигация */}
        <nav className="flex-1 overflow-y-auto py-2">
          {categories.map(category => (
            <div key={category.id} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
              <div className="flex items-center">
                <button
                  onClick={() => handleCategoryClick(category.id)}
                  className={`flex-1 flex items-center justify-between p-3 text-left ${
                    activeCategory === category.id
                      ? (darkMode ? 'bg-blue-900 text-blue-300 font-medium' : 'bg-blue-50 text-blue-700 font-medium')
                      : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {React.cloneElement(category.icon, {
                      className: darkMode ? 'text-blue-400' : 'text-blue-600'
                    })}
                    <span className="text-sm">{category.name}</span>
                  </div>
                  <ChevronDown className={`w-4 h-4 transform transition-transform ${
                    activeCategory === category.id ? 'rotate-180' : ''
                  }`} />
                </button>
                {userRole === 'admin' && category.id !== 'reports' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCategoryToDelete(category);
                    }}
                    className={`p-3 rounded-r-lg ${
                      darkMode
                        ? 'text-gray-400 hover:text-red-400 hover:bg-red-900/30'
                        : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                    }`}
                    title="Удалить раздел"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              {activeCategory === category.id && (
                <div className="pl-12 pr-3 space-y-1 mb-2">
                  {(filteredManuals[category.id] || manuals[category.id] || []).map(manual => (
                    <div key={manual.id} className="flex items-center w-full">
                      <button
                        onClick={() => handleManualClick(manual.id)}
                        className={`flex-1 text-left p-2 rounded-lg text-xs ${
                          activeManual === manual.id
                            ? (darkMode ? 'bg-blue-900 text-blue-300 font-medium' : 'bg-blue-100 text-blue-700 font-medium')
                            : (darkMode ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-700')
                        }`}
                      >
                        {manual.title}
                      </button>
                      {userRole === 'admin' && category.id !== 'reports' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setManualToDelete({ id: manual.id, categoryId: category.id });
                            setShowDeleteManualModal(true);
                          }}
                          className={`p-1.5 ml-1 rounded-lg ${
                            darkMode
                              ? 'text-gray-400 hover:text-red-400 hover:bg-red-900/30'
                              : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                          }`}
                          title="Удалить руководство"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}
                  {userRole === 'admin' && category.id !== 'reports' && (
                    <button
                      onClick={() => {
                        setIsAddingManual(true);
                        if (window.innerWidth < 768) setIsSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-center p-2 rounded-lg mt-1 text-xs ${
                        darkMode
                          ? 'text-blue-400 hover:bg-blue-900/30'
                          : 'text-blue-600 hover:bg-blue-50'
                      }`}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      <span>Новая подвкладка</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
          {userRole === 'admin' && (
            <div className={`p-4 mt-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => {
                  setIsAddingCategory(true);
                  if (window.innerWidth < 768) setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center justify-center p-2 rounded-lg text-sm ${
                  darkMode
                    ? 'bg-blue-900 text-blue-300 hover:bg-blue-800'
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                }`}
              >
                <Plus className="w-4 h-4 mr-2" />
                <span>Добавить раздел</span>
              </button>
            </div>
          )}
        </nav>

        {/* Профиль — исправленное расположение */}
        <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${
                darkMode ? 'from-blue-500 to-blue-700' : 'from-blue-400 to-blue-600'
              } flex items-center justify-center text-white font-bold text-sm`}>
                {currentUser?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex flex-col">
                <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                  {currentUser?.name || 'Пользователь'}
                </span>
                <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {userRole === 'admin' ? 'Администратор' : 'Пользователь'}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setIsChangingPassword(true)}
                className={`p-2 rounded-lg ${
                  darkMode
                    ? 'text-gray-400 hover:text-blue-400 hover:bg-gray-700'
                    : 'text-gray-500 hover:text-blue-600 hover:bg-gray-100'
                }`}
                title="Сменить пароль"
              >
                <Key className="w-4 h-4" />
              </button>
              <button
                onClick={logout}
                className={`p-2 rounded-lg ${
                  darkMode
                    ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700'
                    : 'text-gray-500 hover:text-red-600 hover:bg-gray-100'
                }`}
                title="Выйти"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {isSidebarOpen && window.innerWidth < 768 && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </>
  );
};